import type { FC, HTMLAttributes, ReactNode } from 'react';

import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import cn from 'classnames';

// Shell shared by the composer and the schedule picker, so they keep the same bars and spacing.
type ComposerFrameProps = HTMLAttributes<HTMLDivElement>;

export const ComposerFrame: FC<ComposerFrameProps> = ({ className, ...props }) => (
  <div className={cn('flex flex-1 flex-col min-h-0', className)} {...props} />
);

interface ComposerFrameHeaderProps {
  children?: ReactNode;
  onClose?: () => void;
  closeLabel?: string;
  onBack?: () => void;
  backLabel?: string;
}

const headerButtonClassName = 'flex items-center p-2 hover:bg-black/5 rounded-full transition-colors';

export const ComposerFrameHeader: FC<ComposerFrameHeaderProps> = ({
  children,
  onClose,
  closeLabel,
  onBack,
  backLabel,
}) => (
  <div
    className={cn('flex h-14 items-center justify-between border-b border-black/10 pr-2 py-2', {
      'pl-2': onBack,
      'pl-3': !onBack,
    })}
  >
    <div className={cn('flex min-w-0 items-center', { 'gap-1': onBack, 'gap-2': !onBack })}>
      {onBack && (
        <button type="button" className={headerButtonClassName} onClick={onBack} aria-label={backLabel}>
          <ArrowLeftIcon className="size-6 text-black/50" />
        </button>
      )}
      {children}
    </div>
    {onClose && (
      <button type="button" className={headerButtonClassName} onClick={onClose} aria-label={closeLabel}>
        <XMarkIcon className="size-6 text-black/50" />
      </button>
    )}
  </div>
);

export const ComposerFrameBody: FC<ComposerFrameProps> = ({ className, ...props }) => (
  <div className={cn('flex flex-1 flex-col min-h-0 p-4', className)} {...props} />
);

interface ComposerFrameFooterProps {
  children: ReactNode;
  // Sits above the footer row, inside its top border, like the composer's format bar.
  topBar?: ReactNode;
}

export const ComposerFrameFooter: FC<ComposerFrameFooterProps> = ({ children, topBar }) => (
  <div className="border-t border-black/10">
    {topBar}
    <div className="flex items-center justify-between pl-3 pr-4 py-4">{children}</div>
  </div>
);
